import React, { FC, useRef } from 'react';
import { media } from 'src/styles/designs';
import styled, { css } from 'styled-components';
import { ProductsNeedleSegment } from 'src/modules/products/components/ProductsNeedleSegment';
import { ProductsSectionItems } from 'src/modules/products/components/ProductsSectionItems';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { ProductCategoryCode } from 'src/models/pim';
import { Media } from 'vendor/mods/media-breakpoints-fresnel/components/Media';
import {
  StickyMenuSection,
  StickyMenuSectionProps,
} from 'src/components/common/StickyMenu/StickyMenuSection';
import { dirRTL } from 'src/styles/theme';
import { casePathEmptyString } from 'src/utils/casePathEmptyString';
import Image, { StaticImageData } from 'next/image';
import { useProductsListQuery } from 'src/modules/products/hooks/useProductsListQuery';
import { useDelayedEffect } from 'src/hooks/useDelayedEffect';
import { useRouter } from 'next/router';
import { isNil } from 'rambda';

const Container = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 42px;

  ${media.w.greaterThanOrEqual.desktop} {
    margin-top: 70px;
  }
`;

const ImageContainer = styled.div`
  width: 457px;
  height: 256px;
  position: relative;
`;

const SegmentContainer = styled.div`
  max-width: 400px;
  ${media.w.greaterThanOrEqual.desktop} {
    max-width: 640px;
    margin-left: 115px;
    ${dirRTL} {
      margin-right: 115px;
      margin-left: 0;
    }
  }
`;

const HeadingRow = styled.div<{ $isImageDisplayed: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;

  ${media.w.greaterThanOrEqual.desktop} {
    justify-content: ${({ $isImageDisplayed }) =>
      $isImageDisplayed ? 'center' : 'flex-start'};

    ${casePathEmptyString('$isImageDisplayed')(
      css`
        && {
          ${SegmentContainer} {
            ${media.w.greaterThanOrEqual.desktop} {
              max-width: 420px;
              margin-left: 185px;
              ${dirRTL} {
                margin-right: 185px;
                margin-left: 0;
              }
            }
          }
        }
      `,
    )};
  }
`;

interface ProductsSectionProps {
  category: ProductCategoryCode;
  title: string;
  description: string;
  image?: StaticImageData;
  noOfColumns?: number;
  threshold?: StickyMenuSectionProps['threshold'];
}

export const ProductsInfoSection: FC<ProductsSectionProps> = ({
  category,
  title,
  description,
  image,
  noOfColumns,
  threshold = {
    desktop: 0.5,
    mobile: 0.8,
  },
}) => {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: products } = useProductsListQuery({
    filter: {
      categories: {
        categories_code: { _eq: category },
      },
    },
  });

  useDelayedEffect(() => {
    if (!isNil(router.query[category]) && containerRef.current) {
      window.scrollTo({
        behavior: 'smooth',
        top: containerRef.current.getBoundingClientRect().top - 200,
      });
    }
  }, 250);

  return (
    <StickyMenuSection
      as={ConstraintContainer}
      id={category}
      threshold={threshold}
    >
      <Container>
        <HeadingRow ref={containerRef} $isImageDisplayed={!!image}>
          {image && (
            <Media greaterThanOrEqual="desktop">
              <ImageContainer>
                <Image src={image} layout="fill" sizes="50vw" quality={100} />
              </ImageContainer>
            </Media>
          )}
          <SegmentContainer>
            <ProductsNeedleSegment heading={title} description={description} />
          </SegmentContainer>
        </HeadingRow>
      </Container>

      {products && (
        <ProductsSectionItems
          products={products.data}
          noOfColumns={noOfColumns}
        />
      )}
    </StickyMenuSection>
  );
};
